import React, { useState, useRef } from 'react';
import { t } from 'i18next';
import { exportMyAnnouncements, bulkPatchAnnouncements, patchAnnouncement } from '../../../api/bulk';
import { getUserAdd } from '../../../api/getuserad';
import { getCategories } from '../../../api/getCategories';

function BulkEditModal({ show, onClose }) {
    const [step, setStep] = useState(1);
    const [exporting, setExporting] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [file, setFile] = useState(null);
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');
    const fileInputRef = useRef(null);

    const [inlineAds, setInlineAds] = useState([]);
    const [inlineLoading, setInlineLoading] = useState(false);
    const [inlineSaving, setInlineSaving] = useState(false);
    const [inlineSaved, setInlineSaved] = useState({});
    const [editedAds, setEditedAds] = useState({});
    const [categories, setCategories] = useState([]);

    const handleClose = () => {
        setStep(1);
        setExporting(false);
        setUploading(false);
        setFile(null);
        setResult(null);
        setError('');
        setInlineAds([]);
        setEditedAds({});
        setInlineSaved({});
        onClose();
    };

    const handleExport = async () => {
        setExporting(true);
        setError('');
        try {
            await exportMyAnnouncements({ status: 'ACTIVE' });
            setStep(2);
        } catch (err) {
            setError(err.message || t('fetchFailed') || 'Экспорт сәтсіз аяқталды');
        } finally {
            setExporting(false);
        }
    };

    const handleFileChange = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        const ext = '.' + f.name.split('.').pop().toLowerCase();
        if (!['.xlsx', '.csv', '.tsv'].includes(ext)) {
            setError(t('onlyXlsxAllowed') || 'Тек .xlsx, .csv, .tsv файлдары қабылданады');
            return;
        }
        setFile(f);
        setError('');
        setResult(null);
    };

    const handleUpload = async () => {
        if (!file) return;
        setUploading(true);
        setError('');
        setResult(null);
        try {
            const data = await bulkPatchAnnouncements(file);
            setResult(data);
            setFile(null);
            if (fileInputRef.current) fileInputRef.current.value = '';
        } catch (err) {
            setError(err.message || t('uploadFailed') || 'Жүктеу сәтсіз аяқталды');
        } finally {
            setUploading(false);
        }
    };

    const loadCategories = async () => {
        try {
            const data = await getCategories();
            if (data && Array.isArray(data)) {
                setCategories(data);
            } else if (data && Array.isArray(data.categories)) {
                setCategories(data.categories);
            }
        } catch (err) {
            console.error('Failed to load categories', err);
        }
    };

    const loadInlineAds = async () => {
        setInlineLoading(true);
        setError('');
        try {
            await loadCategories();
            const data = await getUserAdd('active', 1, 100);
            const ads = data?.items || [];
            setInlineAds(ads);
            const initial = {};
            ads.forEach(ad => { initial[ad.id] = buildEditedAd(ad); });
            setEditedAds(initial);
        } catch (err) {
            setError(err.message || 'Жарнамаларды жүктеу сәтсіз');
        } finally {
            setInlineLoading(false);
        }
    };

    const buildEditedAd = (ad) => ({
        title: ad.title || '',
        price: ad.price != null ? String(ad.price) : '',
        currency: ad.currency || 'KZT',
        measurement_unit: ad.measurement_unit || 'piece',
        description: ad.description || '',
        category_id: ad.category_id || '',
        subcategory_id: ad.subcategory_id || '',
        contact_phones: Array.isArray(ad.contact_numbers)
            ? ad.contact_numbers.join(', ')
            : (ad.contact_phones || ad.contact_numbers || ''),
        status: ad.status || 'ACTIVE',
    });

    const handleInlineChange = (id, field, value) => {
        setEditedAds(prev => ({
            ...prev,
            [id]: { ...prev[id], [field]: value },
        }));
    };

    const handleCategoryChange = (id, categoryId) => {
        const categoryIdInt = parseInt(categoryId);
        setEditedAds(prev => ({
            ...prev,
            [id]: {
                ...prev[id],
                category_id: categoryIdInt,
                subcategory_id: '',
            },
        }));
    };

    const handleSaveAd = async (ad) => {
        const data = editedAds[ad.id];
        if (!data) return;
        setInlineSaving(true);
        setError('');
        try {
            const payload = {};
            if (data.title !== ad.title) payload.title = data.title;
            if (data.price !== String(ad.price)) payload.price = Number(data.price);
            if (data.currency !== ad.currency) payload.currency = data.currency;
            if (data.measurement_unit !== ad.measurement_unit) payload.measurement_unit = data.measurement_unit;
            if (data.description !== ad.description) payload.description = data.description;
            if (data.category_id !== ad.category_id) payload.category_id = data.category_id || null;
            if (data.subcategory_id !== ad.subcategory_id) payload.subcategory_id = data.subcategory_id || null;
            if (data.contact_phones !== (ad.contact_phones || ad.contact_numbers || '')) {
                const phones = String(data.contact_phones).split(',').map(p => p.trim()).filter(Boolean);
                payload.contact_numbers = phones.map(p => ({ phone: p }));
            }
            if (Object.keys(payload).length === 0) return;
            await patchAnnouncement(ad.id, payload);
            setInlineSaved(prev => ({ ...prev, [ad.id]: true }));
            setTimeout(() => {
                setInlineSaved(prev => ({ ...prev, [ad.id]: false }));
            }, 2000);
        } catch (err) {
            setError(err.message || 'Сақтау сәтсіз');
        } finally {
            setInlineSaving(false);
        }
    };

    const hasChanges = (ad) => {
        const data = editedAds[ad.id];
        if (!data) return false;
        return data.title !== ad.title ||
            data.price !== String(ad.price) ||
            data.currency !== ad.currency ||
            data.measurement_unit !== ad.measurement_unit ||
            data.description !== ad.description ||
            data.category_id !== ad.category_id ||
            data.subcategory_id !== ad.subcategory_id ||
            data.contact_phones !== (
                Array.isArray(ad.contact_numbers)
                    ? ad.contact_numbers.join(', ')
                    : (ad.contact_phones || ad.contact_numbers || '')
            );
    };

    const getSubcategories = (categoryId) => {
        const cat = categories.find(c => c.id === parseInt(categoryId));
        return cat?.subcategories || [];
    };

    const getCategoryName = (categoryId) => {
        const cat = categories.find(c => c.id === parseInt(categoryId));
        return cat?.name || '';
    };

    const getSubcategoryName = (subcategoryId) => {
        for (const cat of categories) {
            const sub = cat.subcategories?.find(s => s.id === parseInt(subcategoryId));
            if (sub) return sub.name;
        }
        return '';
    };

    const goToInline = () => {
        setStep(3);
        loadInlineAds();
    };

    if (!show) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto">
            <div className="absolute inset-0 bg-black/50" onClick={handleClose} />
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-[1100px] max-h-[90vh] overflow-hidden flex flex-col my-4">

                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
                    <h2 className="text-lg font-semibold text-gray-900">{t('bulkEdit') || 'Массалық редакциялау'}</h2>
                    <button onClick={handleClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Steps indicator */}
                <div className="flex px-6 pt-4 gap-2">
                    {[1, 2, 3].map(n => (
                        <div key={n} className="flex items-center gap-2 flex-1">
                            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                                step >= n ? 'bg-[#147F26] text-white' : 'bg-gray-100 text-gray-400'
                            }`}>{n}</div>
                            <div className={`flex-1 h-0.5 ${n < 3 ? (step > n ? 'bg-[#147F26]' : 'bg-gray-200') : 'hidden'}`} />
                        </div>
                    ))}
                </div>

                {/* Content */}
                <div className="flex-1 overflow-auto p-6">

                    {/* Step 1: Export */}
                    {step === 1 && (
                        <div className="space-y-5">
                            <div>
                                <p className="text-sm font-medium text-gray-800">{t('step1ExportTitle') || '1. Жарнамаларды Excel-ге экспорттаңыз'}</p>
                                <p className="text-xs text-gray-500 mt-1">{t('step1ExportDesc') || 'Барлық белсенді жарнамаларыңыз .xlsx файлына жүктеледі.'}</p>
                            </div>

                            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                                <p className="text-xs text-amber-700 font-medium mb-1">⚠ {t('importantNote') || 'Маңызды'}</p>
                                <ul className="text-xs text-amber-700 space-y-1 list-disc list-inside">
                                    <li>{t('bulkIdRequired') || 'id бағаны міндетті — жойылса жарнама жаңартылмайды'}</li>
                                    <li>{t('bulkMaxRows') || 'Максимум 1000 жол'}</li>
                                    <li>{t('bulkMaxSize') || 'Файл өлшемі: ≤ 10 МБ'}</li>
                                </ul>
                            </div>

                            {error && <p className="text-sm text-red-500">{error}</p>}

                            <button
                                onClick={handleExport}
                                disabled={exporting}
                                className="w-full flex items-center justify-center gap-2 py-3 bg-[#147F26] hover:bg-[#0f6820] disabled:opacity-50 text-white text-sm font-semibold rounded-xl transition-colors"
                            >
                                {exporting ? (
                                    <>
                                        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                        </svg>
                                        {t('exporting') || 'Экспорт жасалуда...'}
                                    </>
                                ) : (
                                    <>
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                        </svg>
                                        {t('exportToExcel') || 'Excel-ге экспорттау'}
                                    </>
                                )}
                            </button>

                            <div className="border-t border-gray-100 pt-4">
                                <p className="text-sm font-medium text-gray-800 mb-2">{t('orEditOnline') || 'Немесе сайтта тікелей өңдеңіз'}</p>
                                <button
                                    onClick={goToInline}
                                    className="w-full flex items-center justify-center gap-2 py-3 border-2 border-[#147F26] text-[#147F26] hover:bg-[#147F26]/5 text-sm font-semibold rounded-xl transition-colors"
                                >
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                    </svg>
                                    {t('editOnSite') || 'Сайтта өңдеу'}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Step 2: Upload edited file */}
                    {step === 2 && (
                        <div className="space-y-5">
                            <div>
                                <p className="text-sm font-medium text-gray-800">{t('step2UploadTitle') || '2. Өңделген файлды жүктеңіз'}</p>
                                <p className="text-xs text-gray-500 mt-1">{t('step2UploadDesc') || 'Excel файлын өңдеп болғаннан кейін, осы жерге жүктеңіз.'}</p>
                            </div>

                            <input ref={fileInputRef} type="file" accept=".xlsx,.csv,.tsv" onChange={handleFileChange} className="hidden" />
                            <div
                                onClick={() => fileInputRef.current?.click()}
                                className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
                                    file ? 'border-[#147F26] bg-[#147F26]/5' : 'border-gray-200 hover:border-[#147F26]'
                                }`}
                            >
                                <svg className={`mx-auto w-9 h-9 mb-2 ${file ? 'text-[#147F26]' : 'text-gray-300'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                                </svg>
                                {file ? (
                                    <p className="text-sm font-medium text-[#147F26]">{file.name}</p>
                                ) : (
                                    <>
                                        <p className="text-sm text-gray-600 font-medium">{t('selectFile') || 'Файлды таңдаңыз'}</p>
                                        <p className="text-xs text-gray-400 mt-1">.xlsx, .csv, .tsv</p>
                                    </>
                                )}
                            </div>

                            {error && <p className="text-sm text-red-500">{error}</p>}

                            {result && (
                                <div className="rounded-xl border border-gray-200 overflow-hidden">
                                    <div className="grid grid-cols-4 divide-x divide-gray-200">
                                        {[
                                            { label: t('total') || 'Барлығы', val: result.total ?? 0, color: 'text-gray-800' },
                                            { label: t('updated') || 'Жаңартылды', val: result.updated ?? 0, color: 'text-[#147F26]' },
                                            { label: t('created') || 'Жасалды', val: result.created ?? 0, color: 'text-blue-500' },
                                            { label: t('errors') || 'Қате', val: result.errors?.length ?? 0, color: 'text-red-500' },
                                        ].map((s, i) => (
                                            <div key={i} className="p-3 text-center">
                                                <p className={`text-xl font-bold ${s.color}`}>{s.val}</p>
                                                <p className="text-[10px] text-gray-400 mt-0.5">{s.label}</p>
                                            </div>
                                        ))}
                                    </div>
                                    {result.errors?.length > 0 && (
                                        <div className="p-4 border-t border-gray-200 space-y-1.5 max-h-36 overflow-y-auto">
                                            {result.errors.map((err, i) => (
                                                <div key={i} className="flex gap-2 text-xs">
                                                    <span className="font-medium text-red-500 flex-shrink-0">{t('row') || 'Жол'} {err.row}:</span>
                                                    <span className="text-gray-600">{err.error}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="flex gap-3">
                                <button
                                    onClick={() => { setStep(1); setFile(null); setResult(null); setError(''); }}
                                    className="flex-1 py-3 border border-gray-200 hover:bg-gray-50 text-gray-600 text-sm font-medium rounded-xl transition-colors"
                                >
                                    {t('back') || 'Артқа'}
                                </button>
                                <button
                                    onClick={handleUpload}
                                    disabled={!file || uploading}
                                    className="flex-1 flex items-center justify-center gap-2 py-3 bg-[#147F26] hover:bg-[#0f6820] disabled:opacity-40 text-white text-sm font-semibold rounded-xl transition-colors"
                                >
                                    {uploading ? (
                                        <>
                                            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                            </svg>
                                            {t('saving') || 'Сақталуда...'}
                                        </>
                                    ) : (
                                        t('saveChanges') || 'Өзгерістерді сақтау'
                                    )}
                                </button>
                            </div>

                            <div className="border-t border-gray-100 pt-4">
                                <button
                                    onClick={goToInline}
                                    className="w-full flex items-center justify-center gap-2 py-3 border-2 border-[#147F26] text-[#147F26] hover:bg-[#147F26]/5 text-sm font-semibold rounded-xl transition-colors"
                                >
                                    {t('editOnSite') || 'Сайтта өңдеу'}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Step 3: Inline editing */}
                    {step === 3 && (
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-gray-800">{t('step3InlineTitle') || 'Жарнамаларды тікелей өңдеу'}</p>
                                    <p className="text-xs text-gray-500 mt-1">{t('step3InlineDesc') || 'Кестедегі өрістерді өзгертіп, жанындағы сақтау батырмасын басыңыз.'}</p>
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={loadInlineAds}
                                        className="px-3 py-1.5 text-xs font-medium border border-gray-200 hover:bg-gray-50 text-gray-600 rounded-lg transition-colors"
                                    >
                                        {t('refresh') || 'Жаңарту'}
                                    </button>
                                </div>
                            </div>

                            {error && <p className="text-sm text-red-500">{error}</p>}

                            {inlineLoading ? (
                                <div className="flex items-center justify-center py-12">
                                    <svg className="animate-spin h-8 w-8 text-[#147F26]" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                    </svg>
                                </div>
                            ) : inlineAds.length === 0 ? (
                                <p className="text-sm text-gray-500 text-center py-8">{t('noAdsFound') || 'Жарнама табылмады'}</p>
                            ) : (
                                <div className="overflow-x-auto border border-gray-200 rounded-xl">
                                    <table className="w-full text-sm min-w-[900px]">
                                        <thead className="bg-gray-50 sticky top-0">
                                            <tr>
                                                <th className="px-2 py-2.5 text-left text-xs font-semibold text-gray-600 w-10">ID</th>
                                                <th className="px-2 py-2.5 text-left text-xs font-semibold text-gray-600 w-20">Сурет</th>
                                                <th className="px-2 py-2.5 text-left text-xs font-semibold text-gray-600 w-48">{t('titleCol') || 'Тақырып'}</th>
                                                <th className="px-2 py-2.5 text-left text-xs font-semibold text-gray-600 w-20">{t('categoryCol') || 'Категория'}</th>
                                                <th className="px-2 py-2.5 text-left text-xs font-semibold text-gray-600 w-20">{t('subcategoryCol') || 'Подкатегория'}</th>
                                                <th className="px-2 py-2.5 text-left text-xs font-semibold text-gray-600 w-20">{t('priceCol') || 'Баға'}</th>
                                                <th className="px-2 py-2.5 text-left text-xs font-semibold text-gray-600 w-16">{t('currencyCol') || 'Валюта'}</th>
                                                <th className="px-2 py-2.5 text-left text-xs font-semibold text-gray-600 w-16">{t('unitCol') || 'Өлшем'}</th>
                                                <th className="px-2 py-2.5 text-left text-xs font-semibold text-gray-600 w-28">{t('phonesCol') || 'Телефон'}</th>
                                                <th className="px-2 py-2.5 text-left text-xs font-semibold text-gray-600 w-10"></th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {inlineAds.map(ad => (
                                                <tr key={ad.id} className="hover:bg-gray-50/50">
                                                    <td className="px-2 py-2 text-xs text-gray-400">#{ad.id}</td>
                                                    <td className="px-2 py-2">
                                                        {ad.main_image_url ? (
                                                            <img src={ad.main_image_url} alt="" className="w-10 h-10 rounded-lg object-cover" />
                                                        ) : (
                                                            <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                                                                <svg className="w-4 h-4 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                                </svg>
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="px-2 py-2">
                                                        <input
                                                            value={editedAds[ad.id]?.title || ''}
                                                            onChange={e => handleInlineChange(ad.id, 'title', e.target.value)}
                                                            className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg outline-none focus:border-[#147F26] transition-colors"
                                                        />
                                                    </td>
                                                    <td className="px-2 py-2">
                                                        <select
                                                            value={editedAds[ad.id]?.category_id || ''}
                                                            onChange={e => handleCategoryChange(ad.id, e.target.value)}
                                                            className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-lg outline-none focus:border-[#147F26] transition-colors bg-white"
                                                        >
                                                            <option value="">—</option>
                                                            {categories.map(cat => (
                                                                <option key={cat.id} value={cat.id}>{cat.name}</option>
                                                            ))}
                                                        </select>
                                                    </td>
                                                    <td className="px-2 py-2">
                                                        <select
                                                            value={editedAds[ad.id]?.subcategory_id || ''}
                                                            onChange={e => handleInlineChange(ad.id, 'subcategory_id', e.target.value)}
                                                            className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-lg outline-none focus:border-[#147F26] transition-colors bg-white"
                                                            disabled={!editedAds[ad.id]?.category_id}
                                                        >
                                                            <option value="">—</option>
                                                            {getSubcategories(editedAds[ad.id]?.category_id).map(sub => (
                                                                <option key={sub.id} value={sub.id}>{sub.name}</option>
                                                            ))}
                                                        </select>
                                                    </td>
                                                    <td className="px-2 py-2">
                                                        <input
                                                            type="number"
                                                            value={editedAds[ad.id]?.price || ''}
                                                            onChange={e => handleInlineChange(ad.id, 'price', e.target.value)}
                                                            className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg outline-none focus:border-[#147F26] transition-colors"
                                                        />
                                                    </td>
                                                    <td className="px-2 py-2">
                                                        <select
                                                            value={editedAds[ad.id]?.currency || 'KZT'}
                                                            onChange={e => handleInlineChange(ad.id, 'currency', e.target.value)}
                                                            className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-lg outline-none focus:border-[#147F26] transition-colors bg-white"
                                                        >
                                                            <option value="KZT">KZT</option>
                                                            <option value="USD">USD</option>
                                                            <option value="RUB">RUB</option>
                                                            <option value="EUR">EUR</option>
                                                        </select>
                                                    </td>
                                                    <td className="px-2 py-2">
                                                        <select
                                                            value={editedAds[ad.id]?.measurement_unit || 'piece'}
                                                            onChange={e => handleInlineChange(ad.id, 'measurement_unit', e.target.value)}
                                                            className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-lg outline-none focus:border-[#147F26] transition-colors bg-white"
                                                        >
                                                            <option value="piece">{t('piece') || 'дана'}</option>
                                                            <option value="kg">{t('kg') || 'кг'}</option>
                                                            <option value="ton">{t('ton') || 'тонна'}</option>
                                                            <option value="liter">{t('liter') || 'литр'}</option>
                                                            <option value="meter">{t('meter') || 'метр'}</option>
                                                            <option value="sqm">{t('sqm') || 'м²'}</option>
                                                        </select>
                                                    </td>
                                                    <td className="px-2 py-2">
                                                        <input
                                                            value={editedAds[ad.id]?.contact_phones || ''}
                                                            onChange={e => handleInlineChange(ad.id, 'contact_phones', e.target.value)}
                                                            placeholder="77001234567"
                                                            className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-lg outline-none focus:border-[#147F26] transition-colors"
                                                        />
                                                    </td>
                                                    <td className="px-2 py-2">
                                                        <button
                                                            onClick={() => handleSaveAd(ad)}
                                                            disabled={inlineSaving || !hasChanges(ad)}
                                                            className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors whitespace-nowrap ${
                                                                inlineSaved[ad.id]
                                                                    ? 'bg-green-500 text-white'
                                                                    : hasChanges(ad)
                                                                        ? 'bg-[#147F26] text-white hover:bg-[#0f6820]'
                                                                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                            }`}
                                                        >
                                                            {inlineSaved[ad.id] ? '✓' : t('save') || 'Сақтау'}
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            <div className="flex gap-3">
                                <button
                                    onClick={() => setStep(2)}
                                    className="flex-1 py-3 border border-gray-200 hover:bg-gray-50 text-gray-600 text-sm font-medium rounded-xl transition-colors"
                                >
                                    {t('back') || 'Артқа'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default BulkEditModal;
